import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  MessageCircle,
  Headphones,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const inquiries = [
  { id: 'sales', label: 'Penawaran Produk' },
  { id: 'support', label: 'Service & Garansi' },
  { id: 'partner', label: 'Kemitraan / Distributor' },
  { id: 'other', label: 'Lainnya' },
];

const DEFAULT_CONTACT = {
  hero_title: 'Mari kita bicara.',
  hero_subtitle:
    'Tim engineer & sales kami siap membantu Anda memilih solusi daya yang paling tepat — gratis tanpa komitmen.',
  hotline: '+62 21 5050 8800',
  hotline_hours: 'Senin–Minggu, 24 jam',
  whatsapp_sales: '+62 812 9000 8800',
  email_support: 'support@i-powerx.com',
  offices: [
    {
      city: 'Head Office — Jakarta',
      address:
        'Menara Jakarta Tower Fortune 12F, Kemayoran, Jakarta — Indonesia',
      phone: '+62 21 5050 8800',
      email: 'support@i-powerx.com',
      map_query: 'Menara+Jakarta+Tower+Fortune+Kemayoran+Jakarta',
    },
    {
      city: 'Kantor Surabaya',
      address:
        'Jl. Dukuh Kupang Barat I No.180-182, Dukuh Pakis, Surabaya — Jawa Timur, Indonesia',
      phone: '+62 31 8417 200',
      email: 'support@i-powerx.com',
      map_query: 'Jl+Dukuh+Kupang+Barat+I+180+Dukuh+Pakis+Surabaya',
    },
  ],
};

export default function Contact() {
  const [contact, setContact] = useState(DEFAULT_CONTACT);

  useEffect(() => {
    axios
      .get(`${API}/api/site/contact`)
      .then((r) => {
        if (r.data && r.data.offices) setContact({ ...DEFAULT_CONTACT, ...r.data });
      })
      .catch(() => {});
  }, []);

  const offices = contact.offices || DEFAULT_CONTACT.offices;

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiry: 'sales',
    message: '',
  });
  const [sending, setSending] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    setSending(true);
    // Mock submission – frontend only phase
    setTimeout(() => {
      setSending(false);
      toast.success('Pesan Anda berhasil dikirim. Tim PowerX akan menghubungi Anda dalam 1×24 jam.');
      setForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        inquiry: 'sales',
        message: '',
      });
    }, 700);
  };

  return (
    <div data-testid="page-contact" className="pt-[68px] md:pt-[100px] bg-white">
      {/* Hero */}
      <section className="relative h-[44vh] min-h-[340px] bg-neutral-950 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              'url(https://images.pexels.com/photos/9300768/pexels-photo-9300768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 h-full flex flex-col justify-center text-white">
          <p className="text-xs tracking-[0.25em] text-red-400 font-semibold mb-4">
            HUBUNGI KAMI
          </p>
          <h1 className="font-display text-5xl md:text-6xl font-bold leading-[1.05] mb-3">
            {contact.hero_title}
          </h1>
          <p className="text-neutral-300 max-w-xl">
            {contact.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Quick contact tiles */}
      <section className="-mt-12 relative z-20 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              icon: Phone,
              title: 'Hotline 24/7',
              info: contact.hotline,
              sub: contact.hotline_hours,
            },
            {
              icon: MessageCircle,
              title: 'WhatsApp Sales',
              info: contact.whatsapp_sales,
              sub: 'Respon < 30 menit',
            },
            {
              icon: Headphones,
              title: 'Email Dukungan',
              info: contact.email_support,
              sub: 'Respon dalam 1×24 jam',
            },
          ].map((c, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-7 border border-neutral-200 shadow-sm hover-lift"
            >
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-4">
                <c.icon className="w-6 h-6 text-px-red" />
              </div>
              <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
                {c.title}
              </p>
              <p className="font-display text-xl font-bold text-neutral-900 mb-1">
                {c.info}
              </p>
              <p className="text-sm text-neutral-600">{c.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form + Info */}
      <section className="section-pad">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-5 gap-12">
          {/* Form */}
          <div className="lg:col-span-3">
            <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
              KIRIM PESAN
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-neutral-900 mb-3">
              Ceritakan kebutuhan Anda.
            </h2>
            <p className="text-neutral-600 mb-8">
              Isi formulir di bawah, dan tim kami akan menghubungi Anda dengan rekomendasi yang sesuai.
            </p>

            <form
              onSubmit={submit}
              className="grid grid-cols-1 md:grid-cols-2 gap-5"
              data-testid="contact-form"
            >
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">
                  Nama Lengkap *
                </label>
                <input
                  required
                  value={form.name}
                  onChange={change('name')}
                  data-testid="contact-name"
                  className="mt-2 w-full bg-neutral-50 border border-neutral-200 focus:border-neutral-900 outline-none rounded-xl px-4 py-3.5 text-sm transition-colors"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">
                  Email *
                </label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={change('email')}
                  data-testid="contact-email"
                  className="mt-2 w-full bg-neutral-50 border border-neutral-200 focus:border-neutral-900 outline-none rounded-xl px-4 py-3.5 text-sm transition-colors"
                  placeholder="anda@perusahaan.com"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">
                  No. Telepon
                </label>
                <input
                  value={form.phone}
                  onChange={change('phone')}
                  data-testid="contact-phone"
                  className="mt-2 w-full bg-neutral-50 border border-neutral-200 focus:border-neutral-900 outline-none rounded-xl px-4 py-3.5 text-sm transition-colors"
                  placeholder="+62 812 xxxx xxxx"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">
                  Perusahaan
                </label>
                <input
                  value={form.company}
                  onChange={change('company')}
                  data-testid="contact-company"
                  className="mt-2 w-full bg-neutral-50 border border-neutral-200 focus:border-neutral-900 outline-none rounded-xl px-4 py-3.5 text-sm transition-colors"
                  placeholder="PT Contoh Indonesia"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">
                  Jenis Pertanyaan *
                </label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {inquiries.map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      onClick={() => setForm({ ...form, inquiry: i.id })}
                      data-testid={`inquiry-${i.id}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        form.inquiry === i.id
                          ? 'bg-neutral-900 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                      }`}
                    >
                      {i.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">
                  Pesan *
                </label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={change('message')}
                  data-testid="contact-message"
                  className="mt-2 w-full bg-neutral-50 border border-neutral-200 focus:border-neutral-900 outline-none rounded-xl px-4 py-3.5 text-sm transition-colors resize-none"
                  placeholder="Ceritakan kebutuhan daya Anda, jumlah unit, atau pertanyaan teknis..."
                />
              </div>
              <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-neutral-500">
                  Dengan mengirim formulir, Anda setuju dengan kebijakan privasi kami.
                </p>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-2 bg-px-red hover:bg-red-700 disabled:opacity-60 text-white font-semibold px-7 py-3.5 rounded-full transition-colors"
                >
                  {sending ? 'Mengirim...' : (
                    <>
                      Kirim Pesan <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Info side */}
          <aside className="lg:col-span-2 space-y-6">
            <div className="bg-neutral-950 text-white rounded-3xl p-8">
              <h3 className="font-display text-xl font-bold mb-5">Jam Operasional</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-px-red mt-0.5" />
                  <div>
                    <p className="font-semibold">Senin – Jumat</p>
                    <p className="text-neutral-400">08.00 – 17.00 WIB</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-px-red mt-0.5" />
                  <div>
                    <p className="font-semibold">Sabtu</p>
                    <p className="text-neutral-400">09.00 – 14.00 WIB</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-px-red mt-0.5" />
                  <div>
                    <p className="font-semibold">Hotline Darurat</p>
                    <p className="text-neutral-400">24/7 — 365 hari</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-neutral-50 rounded-3xl p-8 border border-neutral-200">
              <h3 className="font-display text-xl font-bold text-neutral-900 mb-4">
                Pertanyaan Cepat?
              </h3>
              <p className="text-sm text-neutral-600 mb-5">
                Tim sales kami siap membantu di WhatsApp dengan respon &lt; 30 menit di jam kerja.
              </p>
              <a
                href="https://wa.me/6281290008800"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-full transition-colors text-sm"
              >
                <MessageCircle className="w-4 h-4" /> Chat WhatsApp
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Offices */}
      <section className="bg-neutral-50 section-pad">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
            LOKASI KAMI
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-neutral-900 mb-10">
            Kantor kami di Indonesia.
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {offices.map((o, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-7 border border-neutral-200 hover-lift"
                data-testid={`office-card-${i}`}
              >
                <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
                  {i === 0 ? 'HEAD OFFICE' : 'KANTOR CABANG'}
                </p>
                <h3 className="font-display text-xl font-bold text-neutral-900 mb-4">
                  {o.city}
                </h3>
                <ul className="space-y-3 text-sm text-neutral-700">
                  <li className="flex gap-3">
                    <MapPin className="w-4 h-4 text-px-red flex-shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{o.address}</span>
                  </li>
                  <li className="flex gap-3">
                    <Phone className="w-4 h-4 text-px-red flex-shrink-0 mt-0.5" />
                    <span>{o.phone}</span>
                  </li>
                  <li className="flex gap-3">
                    <Mail className="w-4 h-4 text-px-red flex-shrink-0 mt-0.5" />
                    <span>{o.email}</span>
                  </li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Maps */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-20 grid md:grid-cols-2 gap-5">
          {offices.map((o, i) => (
            <div
              key={i}
              className="rounded-3xl overflow-hidden border border-neutral-200 aspect-[4/3] bg-neutral-100"
            >
              <iframe
                title={`Peta ${o.city}`}
                src={`https://www.google.com/maps?q=${o.map_query || o.mapQuery || ''}&output=embed`}
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid={`contact-map-${i}`}
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
